import { useState, useCallback, useEffect, useRef } from "react";
import api from "../../utils/api";
import {
  fetchAgentConnectionStatus,
  AgentConnectionStatus,
} from "../../api/agent";
import { logger } from "../../utils/logger";
import { safeSetItem, safeRemoveItem } from "../../utils/safeStorage";
import { SessionSyncProgress, SessionSyncMap } from "../../types/chats";

interface SessionData {
  id: string;
  channel: string;
  name?: string;
}

interface UseSessionManagementReturn {
  agentConnectionStatus: AgentConnectionStatus | null;
  hasUserSessions: boolean | null;
  hasJesssSession: boolean | null;
  activeSessionData: SessionData | null;
  setActiveSessionData: (data: SessionData | null) => void;
  isInitializing: boolean;
  syncingSessions: SessionSyncMap;
  updateSessionSync: (sessionSync: SessionSyncProgress) => void;
  removeSessionSync: (sessionId: string) => void;
}

export const useSessionManagement = (
  authUser: any,
  initialActiveSession: SessionData | null,
): UseSessionManagementReturn => {
  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus | null>(null);
  const [hasUserSessions, setHasUserSessions] = useState<boolean | null>(null);
  const [hasJesssSession, setHasJesssSession] = useState<boolean | null>(null);
  const [activeSessionData, setActiveSessionData] =
    useState<SessionData | null>(initialActiveSession);
  const [isInitializing, setIsInitializing] = useState(true);
  const [syncingSessions, setSyncingSessions] = useState<SessionSyncMap>(() => {
    // Restore from localStorage to survive page navigation
    try {
      const saved = localStorage.getItem("syncingSessions");
      if (saved) {
        const entries = JSON.parse(saved);
        // Filter expired entries (> 15 minutes old)
        const now = Date.now();
        const validEntries = entries.filter(
          (e: [string, SessionSyncProgress]) =>
            now - e[1].startTime < 15 * 60 * 1000,
        );
        if (validEntries.length > 0) {
          return new Map(validEntries);
        }
      }
    } catch (e) {
      logger.error(
        "[useSessionManagement] Failed to restore syncingSessions:",
        e,
      );
    }
    return new Map();
  });

  const requestCache = useRef<{
    data: any;
    timestamp: number;
    promise: Promise<any> | null;
  }>({
    data: null,
    timestamp: 0,
    promise: null,
  });

  // ✅ FIX: Track consecutive failures for exponential backoff
  const failureCount = useRef<number>(0);
  const lastSuccessTime = useRef<number>(Date.now());

  // Declare removeSessionSync BEFORE checkAgentConnectionStatus to avoid hoisting issues
  const removeSessionSync = useCallback((sessionId: string) => {
    setSyncingSessions((prev) => {
      const updated = new Map(prev);
      updated.delete(sessionId);
      return updated;
    });
  }, []);

  const checkAgentConnectionStatus = useCallback(async () => {
    try {
      const now = Date.now();
      const CACHE_DURATION = 5000; // 5 seconds cache
      if (requestCache.current.promise) {
        return await requestCache.current.promise;
      }

      if (
        requestCache.current.data &&
        now - requestCache.current.timestamp < CACHE_DURATION
      ) {
        return;
      }

      const requestPromise = Promise.race([
        Promise.all([
          fetchAgentConnectionStatus().catch((err) => {
            failureCount.current += 1;
            return {
              isAgentConnected: false,
              companyId: authUser?.companyID || "",
              timestamp: Date.now(),
            };
          }),
          api.get("/api/get-status").catch((err) => {
            logger.warn(
              "[useSessionManagement] Session status unavailable:",
              err,
            );
            failureCount.current += 1;
            return { data: { session_status: [] } };
          }),
        ]),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Session management request timeout")),
            10000,
          ),
        ),
      ]).catch((err) => {
        failureCount.current += 1;
        //  Only log warning if multiple consecutive failures
        if (failureCount.current <= 2) {
          logger.warn(
            `[useSessionManagement] Request timeout (attempt ${failureCount.current})`,
          );
        } else if (failureCount.current === 3) {
          logger.error(
            "[useSessionManagement] Multiple consecutive timeouts detected, using cached data",
          );
        }
        // Return fallback data on timeout
        return [
          {
            isAgentConnected: false,
            companyId: authUser?.companyID || "",
            timestamp: Date.now(),
          },
          { data: { session_status: [] } },
        ];
      });
      requestCache.current.promise = requestPromise;
      const [agentStatus, sessionStatus] = await requestPromise;

      // Reset failure count on success
      if (sessionStatus.data?.session_status) {
        failureCount.current = 0;
        lastSuccessTime.current = Date.now();
      }

      // Cache the result and clear the promise
      requestCache.current = {
        data: { agentStatus, sessionStatus },
        timestamp: now,
        promise: null,
      };

      const sessions = sessionStatus.data?.session_status || [];
      const hasActiveSessions = Array.isArray(sessions) && sessions.length > 0;

      // Check if there are USER sessions (not JESSS AI session)
      const userSessions = sessions.filter((s: any) => !s.belongs_to_ai);
      const workingUserSessions = userSessions.filter(
        (s: any) => s.status === "WORKING",
      );
      const hasUserSessionsActive = workingUserSessions.length > 0;

      // Check if JESSS session exists
      const jesssSessions = sessions.filter(
        (s: any) => s.belongs_to_ai === true,
      );
      const hasJesssSessionActive = jesssSessions.length > 0;

      // Scope session key by company
      const sessionKey = authUser?.companyID
        ? `activeSessionData:${authUser.companyID}`
        : "activeSessionData";

      // Validate activeSessionData against current working sessions
      // This prevents "stuck" sessions when switching companies or when session becomes invalid
      if (activeSessionData) {
        const match = workingUserSessions.find(
          (s: any) =>
            s.id === activeSessionData.id &&
            s.channel === activeSessionData.channel,
        );

        if (!match) {
          setActiveSessionData(null);
          safeRemoveItem(sessionKey);

          // ✅ FIX: Also clean up invalid sessions from syncingSessions
          // This prevents "stuck" sync progress for disconnected sessions
          removeSessionSync(activeSessionData.id);
        }
      }

      // ✅ FIX: Clean up syncingSessions for sessions that no longer exist
      // Check all sessions in syncingSessions and remove those not in workingUserSessions
      const validSessionIds = new Set(sessions.map((s: any) => s.id));
      Array.from(syncingSessions.keys()).forEach((sessionId) => {
        if (!validSessionIds.has(sessionId)) {
          removeSessionSync(sessionId);
        }
      });

      // Auto-select first WORKING session if none selected and sessions available
      if (hasUserSessionsActive && !activeSessionData) {
        const workingSession = workingUserSessions[0];
        if (workingSession?.id && workingSession?.channel) {
          const sessionData: SessionData = {
            id: workingSession.id,
            channel: workingSession.channel,
            name: workingSession.name,
          };
          setActiveSessionData(sessionData);
          safeSetItem(sessionKey, sessionData);
        }
      }

      // Clear session if no WORKING sessions available
      if (!hasUserSessionsActive) {
        setActiveSessionData(null);
        safeRemoveItem(sessionKey);
      }

      const displayStatus: AgentConnectionStatus = {
        isAgentConnected: hasActiveSessions,
        companyId: agentStatus.companyId || authUser?.companyID || "",
        timestamp: agentStatus.timestamp || Date.now(),
      };

      setAgentConnectionStatus(displayStatus);
      setHasUserSessions(hasUserSessionsActive);
      setHasJesssSession(hasJesssSessionActive);
      setIsInitializing(false);
    } catch (error) {
      logger.error("Error fetching connection status:", error);
      const fallbackStatus: AgentConnectionStatus = {
        isAgentConnected: false,
        companyId: authUser?.companyID || "",
        timestamp: Date.now(),
      };
      setAgentConnectionStatus(fallbackStatus);
      setHasUserSessions(false);
      setHasJesssSession(false);
      setIsInitializing(false);
    }
  }, [
    authUser?.companyID,
    activeSessionData,
    syncingSessions,
    removeSessionSync,
  ]);

  useEffect(() => {
    checkAgentConnectionStatus();

    //  Escutar eventos WebSocket para atualizações em tempo real
    const handleWebSocketSessionUpdate = (event: CustomEvent) => {
      logger.info("[useSessionManagement] WebSocket session update received");
      checkAgentConnectionStatus();
    };

    window.addEventListener(
      "websocket-session-status" as any,
      handleWebSocketSessionUpdate,
    );

    return () => {
      window.removeEventListener(
        "websocket-session-status" as any,
        handleWebSocketSessionUpdate,
      );
    };
  }, [checkAgentConnectionStatus]);

  const updateSessionSync = useCallback((sessionSync: SessionSyncProgress) => {
    setSyncingSessions((prev) => {
      const updated = new Map(prev);
      const existing = updated.get(sessionSync.sessionId);

      // Preserve startTime from first update to prevent UI timer resets
      // Also preserve sessionName and channel if not provided in update
      updated.set(sessionSync.sessionId, {
        ...existing,
        ...sessionSync,
        startTime: existing?.startTime ?? sessionSync.startTime,
        sessionName:
          sessionSync.sessionName || existing?.sessionName || "Unknown",
        channel: sessionSync.channel || existing?.channel || "Waha",
      });

      return updated;
    });
  }, []);

  // Persist syncingSessions to localStorage for cross-page navigation
  useEffect(() => {
    if (syncingSessions.size > 0) {
      localStorage.setItem(
        "syncingSessions",
        JSON.stringify([...syncingSessions.entries()]),
      );
    } else {
      localStorage.removeItem("syncingSessions");
    }
  }, [syncingSessions]);

  return {
    agentConnectionStatus,
    hasUserSessions,
    hasJesssSession,
    activeSessionData,
    setActiveSessionData,
    isInitializing,
    syncingSessions,
    updateSessionSync,
    removeSessionSync,
  };
};
